import { useCallback, useEffect, useState } from 'react';

interface HookApi {
  image: CanvasImageSource | undefined;
  loadImageSrc: (src: Nullable<string> | undefined) => void;
}

export const useFloorSchemaImage = (): HookApi => {
  const [imageSrc, setImageSrc] = useState<Nullable<string>>(null);
  const [image, setImage] = useState<CanvasImageSource | undefined>();

  const loadImageSrc = useCallback((src: Nullable<string> | undefined) => {
    setImageSrc(src ?? null);
  }, []);

  useEffect(() => {
    if (!imageSrc) {
      setImage(undefined);
      return undefined;
    }

    let cancelled = false;

    const loadImage = (useCors: boolean): void => {
      const img = new window.Image();
      if (useCors) img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (!cancelled) setImage(img);
      };
      img.onerror = () => {
        if (cancelled) return;
        if (useCors) {
          // Some floor-plan hosts do not expose CORS headers. Display the image
          // anyway; export will still surface a tainted-canvas error if needed.
          loadImage(false);
          return;
        }
        setImage(undefined);
      };
      img.src = imageSrc;
    };

    loadImage(true);

    return () => {
      cancelled = true;
      setImage(undefined);
    };
  }, [imageSrc]);

  return {
    image,
    loadImageSrc,
  };
};
